import { Navigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { isOmegaAdmin } from '@/features/omega/adminMode';
import { Breadcrumbs } from '@/features/omega/Breadcrumbs';
import { apiFetch } from '@/api/http';

// ── Types ────────────────────────────────────────────────────────────────────

interface ExpenseResponse {
  record_id: string; expense_id: string; brain_id: string;
  service_name: string; category: string;
  amount_usd_cents: number; amount_ils_agorot: number; incurred_at: string;
}
interface ListExpensesResponse { expenses: ExpenseResponse[]; total: number; }

// ── Helpers ──────────────────────────────────────────────────────────────────

export function fmtIls(agorot: number): string { return '₪' + (agorot / 100).toFixed(2); }
export function fmtUsd(cents: number): string { return '$' + (cents / 100).toFixed(2); }
export function fmtDate(iso: string): string {
  return new Date(iso).toLocaleString('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export function categoryColor(cat: string): string {
  if (cat === 'LLM') return '#7dd3fc';
  if (cat === 'infrastructure') return '#a78bfa';
  if (cat === 'api_cost') return '#6ee7b7';
  return 'var(--omega-ink-subtle)';
}

// ── Sub-components ────────────────────────────────────────────────────────────

function StatCard({ label, value, sub, color, highlight }: {
  label: string; value: string | number; sub?: string; color?: string; highlight?: boolean;
}) {
  return (
    <div style={{
      background: highlight ? 'rgba(125,211,252,0.08)' : 'var(--omega-surface, rgba(255,255,255,0.04))',
      border: highlight ? '1px solid rgba(125,211,252,0.4)' : '1px solid var(--omega-rim, rgba(255,255,255,0.08))',
      borderRadius: 6, padding: '14px 18px', minWidth: 140,
    }}>
      <div style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: '18pt', fontWeight: 700, color: color ?? 'var(--omega-ink)' }}>{value}</div>
      {sub && <div style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)', marginTop: 3 }}>{sub}</div>}
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function FinanceExpensesPage() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;

  const { data, isPending, isError } = useQuery({
    queryKey: ['finance', 'expenses'],
    queryFn: () => apiFetch<ListExpensesResponse>('/finance/expenses'),
    refetchInterval: 30_000,
  });

  const topCategories = useMemo(() => {
    if (!data?.expenses) return [];
    const counts: Record<string, number> = {};
    for (const e of data.expenses) {
      counts[e.category] = (counts[e.category] ?? 0) + 1;
    }
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3);
  }, [data]);

  const navLinkStyle: React.CSSProperties = {
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid var(--omega-rim, rgba(255,255,255,0.1))',
    borderRadius: 4, color: 'var(--omega-ink)', fontFamily: 'var(--omega-mono)',
    fontSize: '8.5pt', fontWeight: 600, padding: '6px 14px',
    textDecoration: 'none', letterSpacing: '0.04em',
  };

  return (
    <div className="omega-shell" style={{ fontFamily: 'var(--omega-sans)', color: 'var(--omega-ink)', padding: '16px 20px' }}>
      <Breadcrumbs crumbs={[{ label: 'OMEGA', to: '/omega' }, { label: 'Finance', to: '/omega/finance' }, { label: 'Expenses' }]} />

      <div style={{ marginBottom: 20, marginTop: 8 }}>
        <h1 style={{ margin: 0, fontSize: '12pt', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          Expenses
        </h1>
        <div style={{ fontSize: '9pt', color: 'var(--omega-ink-muted)', marginTop: 4, fontFamily: 'var(--omega-mono)' }}>
          Finance Brain — Expenses
        </div>
      </div>

      {isPending && <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt' }}>Loading…</div>}
      {isError && <div style={{ color: '#f87171', fontSize: '9pt' }}>Error loading data.</div>}

      {data && (
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 28 }}>
          <StatCard label="Total expenses" value={data.total} highlight />
          {topCategories.map(([cat, count]) => (
            <StatCard
              key={cat}
              label={cat}
              value={count}
              color={categoryColor(cat)}
            />
          ))}
        </div>
      )}

      <section>
        <div style={{ fontSize: '9pt', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--omega-ink)', marginBottom: 10, fontFamily: 'var(--omega-mono)' }}>
          Tools
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <Link to="/omega/finance/expenses/list" style={navLinkStyle}>
            Full list
          </Link>
          <Link
            to="/omega/finance/expenses/new"
            style={{ ...navLinkStyle, background: 'rgba(110,231,183,0.1)', border: '1px solid rgba(110,231,183,0.3)', color: '#6ee7b7' }}
          >
            New expense +
          </Link>
        </div>
      </section>
    </div>
  );
}
